import { Search, SlidersHorizontal } from 'lucide-react';
import type { RisingCreatorsFilters, RisingCreatorStatus } from '../../hooks/useRisingCreators';

const STATUS_OPTIONS: { value: RisingCreatorStatus; label: string }[] = [
  { value: 'new', label: 'Nouveau' },
  { value: 'shortlisted', label: 'Présélectionné' },
  { value: 'contacted', label: 'Contacté' },
  { value: 'in_discussion', label: 'En discussion' },
  { value: 'signed', label: 'Signé' },
  { value: 'rejected', label: 'Rejeté' },
];

interface PepitesFiltersProps {
  filters: RisingCreatorsFilters;
  onChange: (filters: RisingCreatorsFilters) => void;
}

export default function PepitesFilters({ filters, onChange }: PepitesFiltersProps) {
  const toggleStatus = (status: RisingCreatorStatus) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter(s => s !== status)
      : [...filters.statuses, status];
    onChange({ ...filters, statuses: newStatuses });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-52">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un créateur..."
              value={filters.search}
              onChange={e => onChange({ ...filters, search: e.target.value })}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Plateforme
          </span>
          {(['all', 'instagram', 'tiktok'] as const).map(p => (
            <button
              key={p}
              onClick={() => onChange({ ...filters, platform: p })}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                filters.platform === p
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {p === 'all' ? 'Tous' : p === 'instagram' ? 'Instagram' : 'TikTok'}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Source</span>
          {(['all', 'hashtag', 'follow_graph', 'tiktok_rising', 'sponsored_collab'] as const).map(s => (
            <button
              key={s}
              onClick={() => onChange({ ...filters, source: s })}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                filters.source === s
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {s === 'all' ? 'Tous' : s === 'hashtag' ? 'Hashtag' : s === 'follow_graph' ? 'Follow graph' : s === 'tiktok_rising' ? 'TikTok Rising' : 'Collab'}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tri</span>
          {([
            { value: 'score', label: 'Score' },
            { value: 'recent', label: 'Plus récent' },
            { value: 'growth', label: 'Croissance' },
          ] as const).map(opt => (
            <button
              key={opt.value}
              onClick={() => onChange({ ...filters, sortBy: opt.value })}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                filters.sortBy === opt.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Followers</span>
          <input
            type="number"
            min={0}
            max={filters.maxFollowers}
            value={filters.minFollowers}
            onChange={e => onChange({ ...filters, minFollowers: Number(e.target.value) })}
            className="w-24 px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Min"
          />
          <span className="text-xs text-gray-400">—</span>
          <input
            type="number"
            min={filters.minFollowers}
            value={filters.maxFollowers}
            onChange={e => onChange({ ...filters, maxFollowers: Number(e.target.value) })}
            className="w-24 px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Max"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Score min</span>
          <input
            type="range"
            min={0}
            max={100}
            value={filters.minScore}
            onChange={e => onChange({ ...filters, minScore: Number(e.target.value) })}
            className="w-24 accent-blue-600"
          />
          <span className="text-xs font-semibold text-blue-600 w-6">{filters.minScore}</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</span>
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => toggleStatus(opt.value)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                filters.statuses.includes(opt.value)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
